import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type StoreStatus = "active" | "suspended" | "hidden";
type ReportStatus = "pending" | "reviewed" | "resolved" | "dismissed";
type ItemStatus = "active" | "inactive";
type AppointmentBookingStatus = "booked" | "cancelled" | "completed" | "no_show" | "late_cancelled";
type EventVisibility = "public" | "unlisted";

type Status = StoreStatus | ReportStatus | ItemStatus | AppointmentBookingStatus | EventVisibility;

const STATUS_STYLES: Record<string, string> = {
  // Store statuses ("active" is shared with item statuses)
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-amber-100 text-amber-800 border-amber-200",
  suspended: "bg-orange-100 text-orange-800 border-orange-200",
  hidden: "bg-gray-100 text-gray-600 border-gray-200",
  // Report statuses
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  reviewed: "bg-blue-100 text-blue-800 border-blue-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  dismissed: "bg-gray-100 text-gray-600 border-gray-200",
  // Appointment booking statuses
  booked: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  no_show: "bg-red-100 text-red-800 border-red-200",
  // Event visibility
  unlisted: "bg-purple-100 text-purple-800 border-purple-200",
  public: "bg-gray-100 text-gray-600 border-gray-200",
};

type StatusBadgeProps = {
  status: Status;
  /** When true and status is "active", renders "Inactive" in amber instead of green. */
  visible?: boolean;
  className?: string;
};

export function StatusBadge({ status, visible, className }: StatusBadgeProps) {
  const derived = status === "active" && visible === false ? "inactive" : status;
  const styles = STATUS_STYLES[derived] ?? "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <Badge variant="outline" className={cn("capitalize font-medium", styles, className)}>
      {derived.replace(/_/g, " ")}
    </Badge>
  );
}
